import { defineStore } from 'pinia'
import type { SocialPlatform } from '~/constants/social-icons'
import type { AppUser } from '~~/types/user'
import { useMessage } from './app-toast'

interface AuthState {
    user: AppUser | null
    loading: boolean
    error: string | null
    signinOut: boolean;
    passwordReseting: boolean;
    passwordResetSending: boolean;
    termsAccepted: boolean;
    termsWarning: boolean;
    emailVerifying: boolean;
    verificationCodeResending: boolean;
    showDelete: boolean;
    deleting: boolean
}

export const useAuthStore = defineStore('AuthStore', {
    state: (): AuthState => ({
        user: null,
        loading: false,
        error: null,
        signinOut: false,
        passwordReseting: false,
        passwordResetSending: false,
        termsAccepted: true,
        termsWarning: false,
        emailVerifying: false,
        verificationCodeResending: false,
        showDelete: false,
        deleting: false
    }),
    actions: {
        toggleTerms() {
            this.termsAccepted = !this.termsAccepted;
        },
        setTermsWarning() {
            this.termsWarning = true
        },
        resetTermsWarning() {
            setTimeout(() => {
                this.termsWarning = false
            }, 2500);
        },
        openDelete() {
            document.body.style.overflow = 'hidden'
            this.showDelete = true
        },
        closeDelete() {
            document.body.style.overflow = 'auto'
            this.showDelete = false
        },
        async checkUser() {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                const response = await $api<any>('/auth/check-auth')
                this.user = response.user
            } catch (err: any) {
                this.user = null
                this.error = err?.message ?? 'Auth check failed'
            } finally {
                this.loading = false
            }
        },
        async verifyEmailUser(verificationCode: string | number): Promise<void> {
            const toast = useMessage()
            const localePath = useLocalePath()
            const { $api, $i18n } = useNuxtApp()

            try {
                this.emailVerifying = true;
                await $api(`/auth/verify-email`, {
                    method: 'POST',
                    body: { verificationCode }
                });
                toast.add(
                    $i18n.t("messages.email_verified_success"),
                    "success",
                );
                setTimeout(async () => {
                    await navigateTo(localePath('/dashboard'))
                }, 1500);
            } catch (e) {
                toast.error(e);
            } finally {
                this.emailVerifying = false;
            }
        },
        async resendVerificationCode(): Promise<void> {
            const { $api, $i18n } = useNuxtApp()
            const localePath = useLocalePath()
            const toast = useMessage()

            this.verificationCodeResending = true;
            const email = this.user?.email;
            if (!email) {
                toast.add(
                    $i18n.t("messages.login_to_continue"),
                    "error",
                );
            }
            try {
                await $api(`/auth/resend-verification-code`, {
                    method: 'POST',
                    body: { email: this.user?.email }
                });
                toast.add(
                    $i18n.t("messages.verification_code_resent"),
                    "success",
                );
                setTimeout(async () => {
                    await navigateTo(localePath('/auth/verify-email'))
                }, 1500);
            } catch (e) {
                toast.error(e);
            } finally {
                this.verificationCodeResending = false;
            }
        },
        async signoutUser(): Promise<void> {
            const { $api, $i18n } = useNuxtApp()
            const localePath = useLocalePath()

            const appStore = useAppStore()
            const toast = useMessage()
            this.signinOut = true;
            try {
                await $api('/auth/logout', {
                    method: 'POST',
                    body: {}
                });

                toast.add($i18n.t("messages.logout_success"),
                    "success",)

                const user = useState<AppUser | null>("auth-user", () => null);
                user.value = null
                await navigateTo(localePath('/gallery'))
                appStore.closeDrawer()
            } catch (e) {
                toast.error(e)
            } finally {
                this.signinOut = false;
            }
        },
        async resetPasswordUser(
            passwordResetCode: string,
            newPassword: string,
            confirmNewPassword: string,
        ): Promise<void> {
            const toast = useMessage()
            const { $api, $i18n } = useNuxtApp()
            this.passwordReseting = true;
            const localePath = useLocalePath()
            try {
                await $api(`/auth/reset-password`, {
                    method: 'POST',
                    body: {
                        passwordResetCode,
                        newPassword,
                        confirmNewPassword
                    }
                });

                toast.add(
                    $i18n.t("messages.password_changed_success"),
                    "success",
                );
                setTimeout(async () => {
                    await navigateTo(localePath('/auth/login'))
                }, 1500);
            } catch (e) {
                toast.error(e);
            } finally {
                this.passwordReseting = false;
            }
        },
        async forgotPasswordUser(email: string): Promise<void> {
            const { $api, $i18n } = useNuxtApp()
            const toast = useMessage()
            const localePath = useLocalePath()

            this.passwordResetSending = true;

            if (!email) {
                toast.add(
                    $i18n.t("messages.email_required"),
                    "error",
                );
            }
            try {
                await $api(`/auth/password-reset-request`, {
                    method: "POST",
                    body: { email }
                });

                toast.add(
                    $i18n.t("messages.password_reset_code_sent"),
                    "success",
                );
                setTimeout(async () => {
                    await navigateTo(localePath('/auth/reset-password'))
                }, 1500);
            } catch (e) {
                toast.error(e)
            } finally {
                this.passwordResetSending = false;
            }
        },

        async deleteAccount(password: string): Promise<void> {
            const { $api, $i18n } = useNuxtApp()
            const localePath = useLocalePath()

            const toast = useMessage()
            this.deleting = true;

            try {
                await $api('/auth/delete-account', {
                    method: 'DELETE',
                    body: { password }
                });

                toast.add($i18n.t("messages.account_deleted_success"),
                    "success",)
                await navigateTo(localePath('/gallery'))
            } catch (e) {
                toast.error(e)
            } finally {
                this.deleting = false;
            }
        },

        updateUserLocalBaseInfo(newUser: AppUser) {
            const { user } = useAuthUser()
            if (newUser && user.value) {
                user.value = newUser
            }
        },

        updateUserAvatar(avatar: string | null) {
            const { updateUserAvatar } = useAuthUser()
            updateUserAvatar(avatar)
        },

        updateUserSocials(socials: Record<SocialPlatform, string>) {
            const { user } = useAuthUser()
            if (user.value) {
                user.value.social_links = socials
            }
        },
    }
})